import {
  mergeTyped,
  useDataStream,
  useNavigator,
  useStreamValue,
  useVoidEventAsStream
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { mapTo } from 'rxjs/operators';
import { createProject } from '../../../api/fetch-functions/project';
import { ProjectAPIRequestBodyType } from '../../../api/types/project/api-request-body';
import { maxStringLength } from '../../../constants/max-string-length';
import { viewTexts } from '../../../constants/view-texts';
import { useFetchResultState } from '../../../functions/hooks/use-fetch-result-state';
import {
  FormInputStateConfigType,
  useFormInputState
} from '../../../functions/hooks/use-form-input-state';
import { useToken, useUserDetail } from '../../../global-state/auth-state';
import { fetchProjectRelatedList } from '../../../global-state/database/combined';
import { fetchProjectListOf } from '../../../global-state/database/project-list';
import { koumutenId$ } from '../../../global-state/router-id';
import { routes } from '../../../routing/routes';
import { IProjectType } from '../../../types/record/project';
import { CreateNewProjectDialogView } from './create-new-project-dialog-view';

const projectNameInputStateConfig: FormInputStateConfigType<ProjectAPIRequestBodyType> = {
  minStringLength: 1,
  maxStringLength: maxStringLength.projectName,
  stringLengthCheckMessageTargetName:
    viewTexts.stringLengthCheckMessageTargetName.projectName,
  required: true,
  validationErrorContentMapFn: content =>
    content.name !== undefined ? content.name.toArray() : []
};

interface Props {
  open: boolean;
  onClose: (reason: 'ok' | 'cancel' | '') => void;
  companyId: number;
}

export const CreateNewProjectDialog: FC<Props> = memo(
  ({ open, onClose, companyId }: Readonly<Props>) => {
    const token = useToken();
    const { name } = useUserDetail();

    /* state */

    const [
      // dummy comment
      fetchResult,
      setFetchResult
    ] = useFetchResultState<IProjectType, ProjectAPIRequestBodyType>();

    const nonFieldErrors = useMemo<string[]>(
      () =>
        fetchResult !== undefined &&
        fetchResult.type === 'error' &&
        fetchResult.body.type === 'non-field-errors'
          ? fetchResult.body.content.toArray()
          : [],
      [fetchResult]
    );

    const [
      projectName$,
      projectNameErrors$,
      projectNameIsValid$,
      projectNameOnInput
    ] = useFormInputState<IProjectType, ProjectAPIRequestBodyType>(
      open,
      fetchResult,
      projectNameInputStateConfig
    );

    const [submitOnClick$, submitOnClick] = useVoidEventAsStream();

    const submitButtonEnabled$ = useDataStream<boolean>(
      false,
      mergeTyped(submitOnClick$.pipe(mapTo(false)), projectNameIsValid$)
    );

    /* extract values */

    const submitButtonEnabled = useStreamValue(submitButtonEnabled$, false);
    const projectName = useStreamValue(projectName$);
    const projectNameErrors = useStreamValue(projectNameErrors$);

    /* event handlers */

    const koumutenId = useStreamValue(koumutenId$);
    const navigator = useNavigator();

    const closeOnClick = useCallback(
      (reason: '' | 'cancel' | 'ok') => {
        if (reason !== 'ok') {
          onClose(reason);
        } else {
          submitOnClick();
          if (
            token !== undefined &&
            koumutenId !== undefined &&
            projectName !== undefined
          ) {
            createProject(token)(companyId)({
              name: projectName,
              companyId
            }).then(res => {
              setFetchResult(res);
              if (res.type === 'success') {
                onClose(reason);
                fetchProjectListOf(companyId);
                const projectId = res.body.id;
                fetchProjectRelatedList(projectId);
                navigator(routes.projectRoot(koumutenId, companyId, projectId));
              }
            });
          }
        }
      },
      [
        onClose,
        navigator,
        koumutenId,
        companyId,
        projectName,
        token,
        setFetchResult,
        submitOnClick
      ]
    );

    return (
      <CreateNewProjectDialogView
        open={open}
        onClose={closeOnClick}
        registererName={name}
        projectName={projectName}
        projectNameErrors={projectNameErrors}
        projectNameOnInput={projectNameOnInput}
        submitButtonEnabled={submitButtonEnabled}
        nonFieldErrors={nonFieldErrors}
      />
    );
  }
);

CreateNewProjectDialog.displayName = 'CreateNewProjectDialog';
